import { nowIsoAndUnix } from "@@/shared/date-time/iso-and-unix";
import type {
    HeadQuoteItem,
    SubAccommodationQuoteItem,
    SubAccommodationRateQuoteItem,
    SubBasicItem,
    SubBookableQuantityQuoteItem,
    SubBookableQuoteItem,
    SubResourceItem,
} from "@@/shared/quotes/quote";
import type { QuoteItemId } from "@@/shared/quotes/quote-item-id";
import { QuoteItemIdFactory } from "@@/shared/quotes/quote-item-id";

/**
 * Creates a duplicate of a head quote item with new IDs and updated timestamps
 * @param originalItem The original head quote item to duplicate
 * @param parentQuoteId Optional new parent quote ID if relevant
 * @returns A new head quote item with fresh IDs
 */
const duplicateHeadQuoteItem = (originalItem: HeadQuoteItem): HeadQuoteItem => {
    // Generate a new ID for the head item
    const newHeadItemId = QuoteItemIdFactory();

    // Create a map to store original sub-item IDs to new sub-item IDs
    const idMap = new Map<QuoteItemId, QuoteItemId>();

    // Duplicate all sub-items with new IDs
    const duplicatedSubItems = originalItem.subItems.map(subItem => {
        const newSubItemId = QuoteItemIdFactory();
        idMap.set(subItem._id, newSubItemId);

        // Based on the type, use the appropriate duplication function
        switch (subItem.type) {
            case "BASIC":
                return duplicateBasicSubItem(subItem);
            case "RESOURCE":
                return duplicateResourceSubItem(subItem, newHeadItemId);
            case "BOOKABLE":
                return duplicateBookableSubItem(subItem);
            case "BOOKABLE_QUANTITY":
                return duplicateBookableQuantitySubItem(
                    subItem,
                    newHeadItemId,
                    idMap,
                );
            case "ACCOMMODATION":
                return duplicateAccommodationSubItem(subItem);
            case "ACCOMMODATION_RATE_ITEM":
                return duplicateAccommodationRateSubItem(
                    subItem,
                    newHeadItemId,
                    idMap,
                );
            default:
                // Generic duplication for unknown types
                return {
                    ...subItem,
                    _id: newSubItemId,
                    _createdAt: nowIsoAndUnix(),
                    _updatedAt: nowIsoAndUnix(),
                    references: {
                        ...subItem.references,
                    },
                };
        }
    });

    // Return the new head item
    return {
        ...originalItem,
        _id: newHeadItemId,
        _createdAt: nowIsoAndUnix(),
        _updatedAt: nowIsoAndUnix(),
        subItems: duplicatedSubItems,
    };
};

/**
 * Duplicates a basic sub-item
 */
const duplicateBasicSubItem = (originalItem: SubBasicItem): SubBasicItem => {
    return {
        ...originalItem,
        _id: QuoteItemIdFactory(),
        _createdAt: nowIsoAndUnix(),
        _updatedAt: nowIsoAndUnix(),
        // Basic items don't have a parentItemId in references
    };
};

/**
 * Duplicates a resource sub-item
 */
const duplicateResourceSubItem = (
    originalItem: SubResourceItem,
    newParentItemId?: QuoteItemId,
): SubResourceItem => {
    return {
        ...originalItem,
        _id: QuoteItemIdFactory(),
        _createdAt: nowIsoAndUnix(),
        _updatedAt: nowIsoAndUnix(),
        references: {
            ...originalItem.references,
            parentItemId:
                newParentItemId || originalItem.references.parentItemId,
        },
    };
};

/**
 * Duplicates a bookable sub-item
 */
const duplicateBookableSubItem = (
    originalItem: SubBookableQuoteItem,
): SubBookableQuoteItem => {
    return {
        ...originalItem,
        _id: QuoteItemIdFactory(),
        _createdAt: nowIsoAndUnix(),
        _updatedAt: nowIsoAndUnix(),
        // Bookable items don't typically have a parentItemId in references
    };
};

/**
 * Duplicates a bookable quantity sub-item with updated references
 */
const duplicateBookableQuantitySubItem = (
    originalItem: SubBookableQuantityQuoteItem,
    newParentItemId: QuoteItemId,
    idMap: Map<QuoteItemId, QuoteItemId>,
): SubBookableQuantityQuoteItem => {
    // Get the new ID for the parent item
    const newParentId = originalItem.references.parentItemId
        ? idMap.get(originalItem.references.parentItemId) || newParentItemId
        : newParentItemId;

    return {
        ...originalItem,
        _id: QuoteItemIdFactory(),
        _createdAt: nowIsoAndUnix(),
        _updatedAt: nowIsoAndUnix(),
        references: {
            ...originalItem.references,
            parentItemId: newParentId,
        },
    };
};

/**
 * Duplicates an accommodation sub-item
 */
const duplicateAccommodationSubItem = (
    originalItem: SubAccommodationQuoteItem,
): SubAccommodationQuoteItem => {
    return {
        ...originalItem,
        _id: QuoteItemIdFactory(),
        _createdAt: nowIsoAndUnix(),
        _updatedAt: nowIsoAndUnix(),
        references: {
            ...originalItem.references,
        },
    };
};

/**
 * Duplicates an accommodation rate sub-item with updated references
 */
const duplicateAccommodationRateSubItem = (
    originalItem: SubAccommodationRateQuoteItem,
    newParentItemId: QuoteItemId,
    idMap: Map<QuoteItemId, QuoteItemId>,
): SubAccommodationRateQuoteItem => {
    // Get the new ID for the parent item
    const newParentId = originalItem.references.parentItemId
        ? idMap.get(originalItem.references.parentItemId) || newParentItemId
        : newParentItemId;

    return {
        ...originalItem,
        _id: QuoteItemIdFactory(),
        _createdAt: nowIsoAndUnix(),
        _updatedAt: nowIsoAndUnix(),
        references: {
            ...originalItem.references,
            parentItemId: newParentId,
        },
    };
};

/**
 * Duplicates an entire array of head quote items
 */
const duplicateQuoteItems = (
    originalItems: HeadQuoteItem[],
): HeadQuoteItem[] => {
    return originalItems.map(item => duplicateHeadQuoteItem(item));
};

export { duplicateHeadQuoteItem, duplicateQuoteItems };
